import React, { useState, useCallback } from 'react';
import { Box, Typography, Avatar, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CalculateIcon from '@mui/icons-material/Calculate';
import { getAllExams, deleteExam } from '../../api/examApi';
import { calculateExamScores, getPartnerAdminExams } from '../../api/adminApi';
import { formatDateUK } from '../../utils/dateUtils';
import { GenericListPage } from '../../components/templates';
import ModernButton from '../../components/common/ModernButton';
import { useToast } from '../../contexts/ToastContext';
import { useAdmin } from '../../contexts/AdminContext';

const ExamList = () => {
  const { showToast } = useToast();
  const { isPlatformAdmin } = useAdmin();
  const [calculateDialog, setCalculateDialog] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [calculationResult, setCalculationResult] = useState(null);

  // Use partner-scoped API for non-platform admins
  const fetchExams = useCallback(async () => {
    if (isPlatformAdmin) {
      return getAllExams();
    }
    const response = await getPartnerAdminExams();
    // Normalize response format
    return { data: response.data?.data || response.data || [] };
  }, [isPlatformAdmin]);

  const handleCalculateScores = async () => {
    if (!calculateDialog) return;
    setCalculating(true);
    try {
      const { data } = await calculateExamScores(calculateDialog._id);
      setCalculationResult(data.data);
      showToast(`Scores calculated successfully for ${data.data.results.success} registrations`, 'success');
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to calculate scores', 'error');
      setCalculateDialog(null);
    } finally {
      setCalculating(false);
    }
  };

  const handleCloseDialog = () => {
    setCalculateDialog(null);
    setCalculationResult(null);
  };

  // Helper to get exam type display info
  const getExamTypeInfo = (examType) => {
    switch (examType) {
      case 'free-practice':
        return { label: 'Free Practice', short: 'FP', color: 'info.main' };
      case 'paid-instant':
        return { label: 'Paid Instant', short: 'PI', color: 'warning.main' };
      case 'paid-scheduled':
        return { label: 'Paid Scheduled', short: 'PS', color: 'success.main' };
      default:
        return { label: 'Unknown', short: '?', color: 'grey.500' };
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Exam Name',
      flex: 1,
      renderCell: (params) => {
        const typeInfo = getExamTypeInfo(params.row.examType);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                backgroundColor: typeInfo.color,
                width: 32,
                height: 32,
                fontSize: '0.75rem',
              }}
            >
              {typeInfo.short}
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {params.value}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {typeInfo.label}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {formatDateUK(params.value)}
        </Typography>
      ),
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {formatDateUK(params.value)}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      renderCell: (params) => {
        const { isActive, isCompleted, isNext } = params.row;
        let status = 'Draft';
        let color = 'default';

        if (isCompleted) {
          status = 'Completed';
          color = 'success';
        } else if (isActive) {
          status = 'Active';
          color = 'primary';
        } else if (isNext) {
          status = 'Upcoming';
          color = 'warning';
        }

        return (
          <Chip
            label={status}
            color={color}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
        );
      },
    },
  ];

  const extraActions = isPlatformAdmin ? (row) => (
    <Tooltip title="Calculate Scores">
      <IconButton
        onClick={() => setCalculateDialog(row)}
        size="small"
        sx={{ color: 'warning.main', '&:hover': { backgroundColor: 'warning.50' } }}
      >
        <CalculateIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : null;

  return (
    <>
      <GenericListPage
        title="Exam Management"
        subtitle={isPlatformAdmin ? "Create and manage IELTS exams and practice tests" : "View exams where your students are registered"}
        icon={<EventNoteIcon />}
        entityName="Exam"
        columns={columns}
        fetchData={fetchExams}
        deleteItem={isPlatformAdmin ? deleteExam : null}
        createPath={isPlatformAdmin ? "/exams/new" : null}
        editPath={isPlatformAdmin ? "/exams" : null}
        extraActions={extraActions}
        tabs={[
          { label: 'All Exams', color: 'default' },
          { label: 'Free Practice', color: 'info' },
          { label: 'Paid Instant', color: 'warning' },
          { label: 'Paid Scheduled', color: 'success' },
        ]}
        filterKey="examType"
        filterValues={[null, 'free-practice', 'paid-instant', 'paid-scheduled']}
        readOnly={!isPlatformAdmin}
      />

      {/* Calculate Scores Dialog */}
      <Dialog open={!!calculateDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {calculationResult ? 'Score Calculation Results' : 'Calculate Exam Scores'}
        </DialogTitle>
        <DialogContent>
          {!calculationResult ? (
            <Box sx={{ py: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                This will calculate Reading and Listening scores for all users registered to:
              </Typography>
              <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                {calculateDialog?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Note: This will check user answers against correct answers and update their band scores accordingly.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ py: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Score calculation completed for <strong>{calculationResult.examName}</strong>
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">
                  Total Registrations: <strong>{calculationResult.results.total}</strong>
                </Typography>
                <Typography variant="body2" color="success.main">
                  Successfully Processed: <strong>{calculationResult.results.success}</strong>
                </Typography>
                {calculationResult.results.failed > 0 && (
                  <Typography variant="body2" color="error.main">
                    Failed: <strong>{calculationResult.results.failed}</strong>
                  </Typography>
                )}
              </Box>
              {calculationResult.results.errors?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    Errors:
                  </Typography>
                  {calculationResult.results.errors.map((err, idx) => (
                    <Typography key={idx} variant="caption" color="error" sx={{ display: 'block' }}>
                      - {err.error}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <ModernButton onClick={handleCloseDialog}>
            {calculationResult ? 'Close' : 'Cancel'}
          </ModernButton>
          {!calculationResult && (
            <ModernButton
              variant="contained"
              color="warning"
              onClick={handleCalculateScores}
              disabled={calculating}
              startIcon={calculating ? <CircularProgress size={16} color="inherit" /> : <CalculateIcon />}
            >
              {calculating ? 'Calculating...' : 'Calculate Scores'}
            </ModernButton>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExamList;
